import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { useState } from "react";

export interface FormField {
  key: string;
  label: string;
  type: "text" | "email" | "password" | "select";
  options?: { value: string; label: string }[];
  hidden?: (values: Record<string, string>) => boolean;
  validate?: (value: string, values: Record<string, string>) => string | undefined;
}

interface CrudFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, string>) => Promise<void>;
  title: string;
  fields: FormField[];
  submitLabel?: string;
}

const buildEmpty = (fields: FormField[]): Record<string, string> =>
  Object.fromEntries(fields.map((f) => [f.key, f.options?.[0]?.value ?? ""]));

const CrudFormModal = ({
  open,
  onClose,
  onSubmit,
  title,
  fields,
  submitLabel = "Guardar",
}: CrudFormModalProps) => {
  const [values, setValues] = useState<Record<string, string>>(() => buildEmpty(fields));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const set = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    for (const field of fields) {
      if (field.hidden?.(values)) continue;
      const msg = field.validate?.(values[field.key] ?? "", values);
      if (msg) next[field.key] = msg;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await onSubmit(values);
      handleClose();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const next: Record<string, string> = {};
        for (const d of detail) {
          const field = d.loc?.[d.loc.length - 1];
          if (field) next[field] = d.msg;
        }
        setErrors(next);
      } else {
        setErrors({ _global: typeof detail === "string" ? detail : "Error inesperado." });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setValues(buildEmpty(fields));
    setErrors({});
    onClose();
  };

  const visibleFields = fields.filter((f) => !f.hidden?.(values));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
        {errors._global && (
          <FormHelperText error>{errors._global}</FormHelperText>
        )}
        {visibleFields.map((field) => {
          if (field.type === "select") {
            return (
              <FormControl key={field.key} fullWidth size="small" error={!!errors[field.key]}>
                <InputLabel>{field.label}</InputLabel>
                <Select
                  value={values[field.key] ?? ""}
                  label={field.label}
                  onChange={(e) => set(field.key, e.target.value)}
                >
                  {field.options?.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors[field.key] && <FormHelperText>{errors[field.key]}</FormHelperText>}
              </FormControl>
            );
          }
          return (
            <TextField
              key={field.key}
              label={field.label}
              type={field.type}
              value={values[field.key] ?? ""}
              onChange={(e) => set(field.key, e.target.value)}
              error={!!errors[field.key]}
              helperText={errors[field.key]}
              fullWidth
              size="small"
            />
          );
        })}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading} disableElevation>
          {loading ? "Guardando…" : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrudFormModal;
